import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import BackIcon from '@src/svgs/BackIcon';
import CommentsIcon from '@src/svgs/CommentsIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import InstagramBookmarkIcon from '@src/svgs/InstagramBookmarkIcon';
import LikesIcon from '@src/svgs/LikesIcon';
import LinkedInShareIcon from '@src/svgs/LinkedInShareIcon';
import React from 'react';
import styled from 'styled-components';

import { BrandLogo, BrandName, StoryDescription } from '../BrandSlots';
import { useChromeFontSize } from '../useChromeFontSize';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.linkedinShortFormVideo;

/**
 * LinkedIn Shorts overlay — paints LinkedIn's full-screen vertical short-video
 * surface chrome over the clip-preview rect: top back-nav, right-side action
 * rail (like / comment / send / bookmark / more), bottom author strip
 * (avatar + name + Follow pill, subtitle, caption preview), an optional
 * caption block, and a thin always-on scrubber line at the very bottom.
 *
 * 9:16 only. Non-9:16 uploads (1:1 / 4:5) are out of scope today; LinkedIn
 * does center them with letterbox bands on the real surface, but the
 * synthetic-viewport plumbing for that was removed when 1:1 was pulled.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from
 * **both** rect dimensions. Reference rect **360×640** → 1em = 10px.
 * Scale factor `min(rect.width / 360, rect.height / 640)`. There is no
 * lower floor — the chrome shrinks linearly with the rect so the entire
 * overlay stays in proportion at every preview size. The upper cap
 * (`SCALE_MAX`) is still in place so absurdly-large previews don't render
 * absurdly-large chrome. `SCALE_FLOOR` is a tiny `0.05` safety floor (≈
 * 0.5px font) so a zero-area rect doesn't produce a `0` font-size.
 */
const REFERENCE_RECT_WIDTH = 360;
const REFERENCE_RECT_HEIGHT = 640;
const REFERENCE_FONT_PX = 10;
const SCALE_FLOOR = 0.05;
const SCALE_MAX = 3;

export const LinkedInShortsOverlay: React.FC<SafeZoneProps> = ({
  rect,
  variants,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_RECT_WIDTH,
    referenceHeight: REFERENCE_RECT_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_FLOOR,
    scaleMax: SCALE_MAX,
  });

  const showCaptions = !!variants.showCaptions;

  // Always-on bottom scrubber — hooks into the shared drag-and-seek hook
  // (same one Feed Post uses) so seek behavior stays in lockstep across
  // overlays. LinkedIn shorts has no player-controls bar; the line at the
  // very bottom of the screen is the only playback affordance.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);
  const duration = playback?.duration ?? 0;

  return (
    // Frame fills the video rect (9:16). All chrome positions resolve
    // against the Frame's bounds.
    <Frame
      data-testid="linkedin-shorts-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      <TopStrip>
        <BackButton type="button" aria-label={COPY.aria.back}>
          <BackIcon width="2em" height="1.4em" strokeColor="#ffffff" />
        </BackButton>
      </TopStrip>

      <RightRail>
        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.like}>
            <FlippedGlyph>
              <LikesIcon width="2.6em" height="2.6em" strokeColor="#ffffff" />
            </FlippedGlyph>
          </ActionIconBtn>
          <ActionCount>{COPY.counts.like}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.comment}>
            <CommentsIcon width="2.6em" height="2.6em" strokeColor="#ffffff" />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.comment}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.share}>
            <LinkedInShareIcon
              width="2.6em"
              height="2.6em"
              strokeColor="#ffffff"
            />
          </ActionIconBtn>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.bookmark}>
            <InstagramBookmarkIcon
              width="2.4em"
              height="2.4em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
        </ActionItem>

        <MoreActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.more}>
            <EllipsisIcon width="2.4em" height="0.5em" fillColor="#ffffff" />
          </ActionIconBtn>
        </MoreActionItem>
      </RightRail>

      {/* Caption — always mounted, fades in/out via opacity */}
      <CaptionAnchor $visible={showCaptions}>
        <CaptionBox>
          <CaptionLines>{COPY.captionPlaceholder}</CaptionLines>
        </CaptionBox>
      </CaptionAnchor>

      <BottomStrip>
        <BrandRow>
          <Avatar>
            <BrandLogo />
          </Avatar>
          <BrandTextStack>
            <BrandNameLine>
              <BrandNameText>
                <BrandName />
              </BrandNameText>
              <FollowPill type="button" aria-label={COPY.aria.follow}>
                {COPY.followLabel}
              </FollowPill>
            </BrandNameLine>
            <BrandSubtitle>{COPY.subtitle}</BrandSubtitle>
          </BrandTextStack>
        </BrandRow>
        <CaptionPreview>
          <StoryDescription fallback={COPY.descriptionFallback} />
          {/*
            "...more" pinned to the right of the description row, so it
            stays in the same spot regardless of how short / long the
            description is. Its font size is fixed in px (not em) so it
            doesn't grow as the rect scales — LinkedIn's "...more"
            affordance is small and constant on the real surface.
          */}
          <MoreLink>{COPY.moreLink}</MoreLink>
        </CaptionPreview>
      </BottomStrip>

      {/* Always-on thin scrubber at the very bottom of the viewport — no
          thumb dot. Pointer-events activate when the playback bridge is
          ready, so the user can drag exactly like the editor's playhead. */}
      <ScrubberRail
        ref={scrubberRef}
        role="slider"
        aria-label={COPY.aria.seek}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, duration)}
        aria-valuenow={Math.max(0, displayTime)}
        onMouseDown={onScrubberMouseDown}
        $interactive={scrubberInteractive}
      >
        <ScrubberFill style={{ width: `${progressPct}%` }} />
      </ScrubberRail>
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 640px viewport reference).
// ---------------------------------------------------------------------------

// Frame fills the rect — chrome positions are absolute against this box.
const Frame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  line-height: 1;
  color: #ffffff;
`;

const TopStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5em;
  padding: 1.6em 1.6em 0;
  display: flex;
  align-items: center;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const BackButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.4em;
  margin: -0.4em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
`;

// Right-side action rail. Bottom-anchored at the brand-row title baseline.
// `z-index: 1` lifts it above the bottom strip's gradient backdrop — the
// rail and BottomStrip are siblings, but BottomStrip renders later in
// JSX, so without a stacking-context bump its `rgba(0,0,0,0.65)` gradient
// would dim the rail's white icons where they overlap the strip.
const RightRail = styled.div`
  position: absolute;
  right: 0.8em;
  bottom: 5.8em;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.8em;
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
`;

// Dedicated wrapper for the three-dots / "more" action — sits below the
// rest of the rail with an extra `0.72em` gap (40% of the standard 1.8em
// inter-icon gap) so the ellipsis reads as a distinct affordance,
// mirroring LinkedIn's actual rail rhythm. A dedicated component instead
// of a `:last-child` selector so the spacing survives any future reorder
// of rail items.
const MoreActionItem = styled(ActionItem)`
  margin-top: 0.72em;
`;

// Thicken every stroked path inside the rail's icons by one unit. Most
// shared icons (LikesIcon, CommentsIcon) render at the default
// `stroke-width: 1`; this CSS override bumps them to 2 so the rail reads
// as confidently as the bookmark icon (which already ships at 2).
// Filled glyphs (EllipsisIcon) are unaffected — they have no stroke.
const ActionIconBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));

  & svg path,
  & svg circle,
  & svg rect {
    stroke-width: 2;
  }
`;

// Mirrors the icon horizontally — LinkedIn's thumbs-up on the shorts surface
// points the opposite way from our shared `LikesIcon`.
const FlippedGlyph = styled.span`
  display: inline-flex;
  transform: scaleX(-1);
`;

const ActionCount = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Bottom strip — author / brand metadata. `bottom` matches the scrubber
// rail's height (0.25em) so the strip's lower edge sits flush against the
// scrubber's top edge — no gap, no overlap.
const BottomStrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.25em;
  padding: 1.6em 1.6em 1.6em;
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.65) 0%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  /* Reserve right-edge clearance so the bottom strip never tucks underneath
     the action rail. */
  padding-right: 5.5em;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9em;
`;

// Avatar tall enough to span both the brand name and follower count rows
// (matches LinkedIn's shorts-surface layout). Sized in `em` so it tracks
// the rest of the chrome under the viewport-derived font scale.
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 4.2em;
  height: 4.2em;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.1em rgba(255, 255, 255, 0.85) inset;
`;

// Stacks brand-name line (top) + subtitle (bottom) beside the avatar.
// `min-width: 0` lets the inner ellipsis work; `flex: 1 1 auto` lets the
// stack absorb available space. Gap is `0.4em` (~4px at reference scale)
// — 2px more than the original 0.2em so the subtitle sits a hair clear of
// the account name.
const BrandTextStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.4em;
`;

// First line of the brand-text stack — name + Follow pill on the same row.
// The name flexes to take available space and ellipsizes when narrow,
// keeping the pill anchored snug against the end of the name.
const BrandNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7em;
  min-width: 0;
`;

// `display: block` so `text-overflow: ellipsis` works reliably across
// browsers — spans are inline by default, and ellipsis truncation needs a
// block-formatted box.
const BrandNameText = styled.span`
  display: block;
  font-size: 1.3em;
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Follow pill sits inline after the brand name (LinkedIn shorts surface
// puts it adjacent to the account name, not on a separate row).
const FollowPill = styled.button`
  pointer-events: auto;
  flex: 0 0 auto;
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.3em 0.9em;
  border: 0.1em solid #ffffff;
  border-radius: 999em;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// `display: block` so `text-overflow: ellipsis` works reliably — same
// reason as `BrandNameText` above.
const BrandSubtitle = styled.span`
  display: block;
  font-size: 1.1em;
  font-weight: 400;
  opacity: 0.9;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// `position: relative` so the absolutely-positioned MoreLink anchors to
// the description row. `padding-right` reserves a gutter wide enough for
// "...more" at the same font size as the description (scales with chrome).
const CaptionPreview = styled.div`
  position: relative;
  font-size: 1.1em;
  font-weight: 400;
  line-height: 1.35;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  padding-right: 4em;
  /* Two-line preview with truncation. */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

// Stationary at the right edge of the description row. Inherits the
// description's font-size so "...more" reads at the exact same size as
// the description text to its left.
const MoreLink = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: inherit;
  line-height: 1.35;
  opacity: 0.8;
`;

// Captions sized like Feed Post's landscape (16:9) variant — small,
// centered. Anchored just above the brand row; the caption-to-logo gap
// is `bottom (10.9em) − brand-row-top (~9.65em)` ≈ `1.25em` — another
// ~10% tighter than the prior `11em` setting.
const CaptionAnchor = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10.9em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease-out;
`;

// Caption box width is em-based (not a `%` of the rect) so it tracks the
// chrome's scale rather than the rect's literal width. At reference
// (1em = 10px, rect.width = 360) `18em` ≈ 180px ≈ 50% of rect width — and
// stays at that proportion under every clamp/aspect drift, since `fontPx`
// itself is derived from `min(width, height)`.
const CaptionBox = styled.div`
  display: inline-block;
  color: #ffffff;
  font-size: 1.3em;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.35;
  text-align: center;
  max-width: 18em;
`;

const CaptionLines = styled.span`
  display: inline;
  background: rgba(0, 0, 0, 0.92);
  padding: 0.1em 0.4em;
  border-radius: 0.2em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// Thin always-on scrubber line — no thumb. Mirrors the editor's playhead
// rail. Pointer-events flip to `auto` only when there's a real bridge to
// drive, so the rest of the overlay stays click-through.
// Unfilled portion of the rail is a solid dark gray (not a translucent
// white) so the playhead reads cleanly against any frame — LinkedIn's
// shorts surface paints the unfilled track as opaque rather than letting
// the video bleed through.
const ScrubberRail = styled.div<{ $interactive: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 0.25em;
  background: #5a5a5a;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
`;
